import { Injectable } from '@angular/core';
import { ActivatedRoute, Params } from '@angular/router';
import { chain, filter, find, isEmpty, keys } from 'lodash';
import { combineLatest, Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { FilterFieldValue } from './covance-filter.models';
import { CovanceFilterChainNodeType } from './filter-type/covance-filter-chain-node-type';

export type FilteredEntityField<T> = {
    title: string;
    fieldName: keyof T;
};

export interface ResultFilteredData<T> {
    entities: T[];
    deserializedFieldValues: FilterFieldValue[];
}

@Injectable()
export class CovanceChainFilter {

    constructor(
        private route: ActivatedRoute,
        private filterChainNodeType: CovanceFilterChainNodeType
    ) { }

    public getFilteredData<T>(
        list: Observable<T[]>,
        listFilteredEntityFields: Observable<FilteredEntityField<T>[]>,
        initFilterFieldValues: Observable<FilterFieldValue[]> = of([])
    ): Observable<ResultFilteredData<T>> {
        return combineLatest(list, listFilteredEntityFields, this.route.queryParams, initFilterFieldValues)
            .pipe(
                map(([entities, entityFields, allParams, initFilterFieldVals]: [T[], FilteredEntityField<T>[], Params,
                    FilterFieldValue[]]) => {
                    const deserializedFieldValues: FilterFieldValue[] = [];

                    if (entities === null) {
                        return { entities, deserializedFieldValues };
                    }

                    const queryParams = this.getInitQueryParams(allParams, initFilterFieldVals);

                    let lastFilteredEntities = entities;

                    chain(queryParams)
                        .keys()
                        .filter(x => this.filterChainNodeType.tryParseChainIndex(x))
                        .sort()
                        .forEach(paramKey => {
                            const paramValue = this.filterChainNodeType.decodeQueryParam(queryParams[paramKey]);
                            const parsedQueryValue = this.filterChainNodeType.parseQueryValue(paramValue);

                            if (!parsedQueryValue) {
                                return;
                            }

                            const [title, filteredFieldName, values] = parsedQueryValue;

                            const entityField = find(entityFields, x => x.fieldName === filteredFieldName);

                            if (!entityField) {
                                return;
                            }

                            const fieldVal = this.getFilterFieldValue<T>(initFilterFieldVals, paramKey, title, entityField.fieldName);
                            fieldVal.def.getItems = (filteredEntities: T[]) => {
                                const items = chain(filteredEntities)
                                    .map(entity => entity[entityField.fieldName])
                                    .uniq()
                                    .value();

                                return items;
                            };

                            fieldVal.queryParamValue = paramValue;
                            this.filterChainNodeType.setValue(fieldVal);

                            const currentFiltered = filter(
                                lastFilteredEntities,
                                (entity: T) => {
                                    const item = entity as any;
                                    return this.filterChainNodeType.isMatch(fieldVal, item[filteredFieldName]);
                                });
                            this.filterChainNodeType.setItems(fieldVal, lastFilteredEntities);

                            lastFilteredEntities = currentFiltered;

                            deserializedFieldValues.push(fieldVal);
                        })
                        .value();

                    return { entities: lastFilteredEntities, deserializedFieldValues };
                })
            );
    }

    private getInitQueryParams(allParams: Params, initFilterFieldVals: FilterFieldValue[]): Params {
        const queryParams: Params = {};

        keys(allParams).forEach(key => {
            queryParams[key] = allParams[key];
        });

        for (let initFilterFieldVal of initFilterFieldVals) {
            if (queryParams[initFilterFieldVal.def.queryParam]) {
                continue;
            }

            this.filterChainNodeType.setQueryParamValue(initFilterFieldVal);

            queryParams[initFilterFieldVal.def.queryParam] = initFilterFieldVal.queryParamValue;
        }

        return queryParams;
    }

    private getFilterFieldValue<T>(initFilterFieldVals: FilterFieldValue[], paramKey: string, title: string, filteredFieldName: keyof T): FilterFieldValue {
        if (isEmpty(initFilterFieldVals)) {
            return this.filterChainNodeType.createFilterFieldValue(paramKey, title, filteredFieldName as string);
        }

        const filterFieldVal = find(initFilterFieldVals, f => f.def.queryParam === paramKey);

        if (!filterFieldVal) {
            return this.filterChainNodeType.createFilterFieldValue(paramKey, title, filteredFieldName as string);
        }

        return filterFieldVal;
    }
}